import React from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { Box } from '../../../components/Box/Box';
import { Button } from '../../../components/Button/Button';
import { Layout } from '../../../components/Layout/Layout';
import {
  CELL_ALIGNMENTS,
  CELL_PX,
  CELL_PY,
  CELL_STICKY_ZINDEX,
  ROW_DETAILS_CLASSNAME,
} from '../constants';
import { CellType, ColumnAlign } from '../types';
import { renderers } from './renderers';

interface Props {
  align?: ColumnAlign;
  as?: 'td' | 'th';
  cellProps?: any; // TODO [EKP-10675]: strongly type based on props.type
  className?: string; // TODO: research and deprecate API
  disabled?: boolean;
  flex?: 'none' | 'auto';
  isActive?: boolean;
  isPadded?: boolean;
  isPrimary?: boolean;
  stickyLeft?: number;
  stickyRight?: number;
  style?: object; // TODO: research and deprecate API
  type?: CellType;
  width?: string; // override width
  render?: (cell: object, type: CellType) => React.ReactElement; // TODO [EKP-10675]: strongly type based on props.type
  onClick?: () => void;
  onShowDetails?: () => void; // the primary cell supports a special show details action.
}

export const Cell = ({
  align = 'left',
  as = 'td',
  className,
  cellProps,
  disabled,
  flex,
  isActive,
  isPadded = true,
  isPrimary,
  stickyLeft,
  stickyRight,
  style = {},
  type = 'text',
  width,
  render,
  onShowDetails,
  onClick,
  ...rest
}: Props) => {
  const intl = useIntl();
  const styles = useStyles();

  const enableShowDetails = isPrimary && onShowDetails;

  const renderer = render || renderers[type];
  const justify = enableShowDetails ? 'space-between' : CELL_ALIGNMENTS[align]; // always override justify if details are enabled

  const isSticky =
    Number.isInteger(stickyLeft) || Number.isInteger(stickyRight);

  const stickyStyles = {
    position: 'sticky',
    left: stickyLeft,
    right: stickyRight,
    zIndex: CELL_STICKY_ZINDEX,
  };

  return (
    <Layout
      {...rest}
      align="center"
      borderRight={isPrimary ? 'border.divider' : undefined}
      as={as}
      className={className}
      flex={flex}
      justify={justify}
      px={isPadded ? CELL_PX : 0}
      py={isPadded ? CELL_PY : 0}
      styles={{
        ...style,
        ...(isSticky ? stickyStyles : {}),
        ...componentStyles.cell,
        ...(disabled ? styles.disabledVisual : {}),
        ...(isActive
          ? styles.colors.status({
              status: 'active',
            })
          : {}),
        width,
      }}
      onClick={onClick}
    >
      {renderer(cellProps, type)}
      {enableShowDetails && (
        <Box
          className={ROW_DETAILS_CLASSNAME}
          styles={componentStyles.detailsAction}
        >
          <Button
            icon="chevron-right"
            text={intl.formatMessage({
              id: 'Table.Cell.showDetailsbutton',
              defaultMessage: 'Details',
              description: 'Button to show details of a row in a table',
            })}
            variant="tertiary"
            onClick={onShowDetails}
          />
        </Box>
      )}
    </Layout>
  );
};

const componentStyles = {
  cell: {
    background: 'inherit',
    boxShadow: 'inherit',
    display: 'inline-flex',
  },
  detailsAction: {
    visibility: 'hidden',
  },
};
